import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import { cn } from '../../lib/cn';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** Card content */
  children: ReactNode;
  /** Enable hover effect */
  hover?: boolean;
  /** Enable press animation */
  pressable?: boolean;
  /** Padding size */
  padding?: 'none' | 'sm' | 'md' | 'lg';
  /** Variant for different card styles */
  variant?: 'default' | 'elevated' | 'outlined';
  /** Additional CSS classes */
  className?: string;
}

const paddingClasses = {
  none: '',
  sm: 'p-3',
  md: 'p-4',
  lg: 'p-6',
};

const variantClasses = {
  default: 'bg-surface border border-border shadow-card',
  elevated: 'bg-surface border-0 shadow-card-hover',
  outlined: 'bg-transparent border border-border shadow-none',
};

/**
 * Card component for admin dashboard.
 * Simpler than web version - CSS-only transitions for better performance.
 */
export const Card = forwardRef<HTMLDivElement, CardProps>(
  function Card(
    {
      children,
      hover = false,
      pressable = false,
      padding = 'md',
      variant = 'default',
      className,
      ...props
    },
    ref
  ) {
    return (
      <div
        ref={ref}
        className={cn(
          variantClasses[variant],
          'rounded-card transition-all duration-fast',
          paddingClasses[padding],
          hover && 'hover:shadow-card-hover hover:-translate-y-0.5 hover:border-action/20',
          pressable && 'cursor-pointer active:scale-[0.99]',
          className
        )}
        {...props}
      >
        {children}
      </div>
    );
  }
);
